import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AINormalInput from '../../../ui/inputs/AINormalInput';
import '../../../styles/pages/notifications/InsightItemConfig.scss';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import SettingsIconSelectInput from '../../../ui/inputs/SettingsIconSelectInput';
import {
  calendarSVG,
  clockSVG,
  editSVG,
  plusSignSVG,
} from '../../../lib/allCustomSVGs';
import {
  update_graph_api_call,
  GraphApi,
  GraphUpdateRequest,
  Granularity,
} from '../../../lib/api/Reports/report_graph_api_call';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import SquareColorInput from '../../../ui/inputs/SquareColorInput';
import MetricSelection from './MetricSelection';

type Props = {
  togglePopupModal: (isOpen: boolean) => void;
  onGraphUpdated?: () => void;
  integrationsAndMetrics: Array<{
    id: string; // slug
    mainTitle: string;
    mainIcon?: string;
    items: Array<{ title: string; value?: string; icon?: any }>;
  }>;
  graph: GraphApi;
};

const MAX_METRICS = 3;

/** extract slug from short-code: tara.insight.<slug>.<metric> */
const slugFromShortcode = (sc: string) => {
  const m = /^tara\.insight\.([^.]+)\./i.exec(sc || '');
  return m?.[1] || '';
};

const EditReportsGraphs = ({
  togglePopupModal,
  onGraphUpdated,
  integrationsAndMetrics,
  graph,
}: Props) => {
  const { t } = useTranslation();

  const fromGranularity = (g: Granularity): 'daily' | 'weekly' | 'monthly' =>
    g === 'per_week' ? 'weekly' : g === 'per_month' ? 'monthly' : 'daily';

  const toGranularity = (p: 'daily' | 'weekly' | 'monthly'): Granularity =>
    p === 'weekly' ? 'per_week' : p === 'monthly' ? 'per_month' : 'per_day';

  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ----- Core fields -----
  const [graphName, setGraphName] = useState(graph.name);

  // Final selected metrics (short-codes)
  const [metricsSelected, setMetricsSelected] = useState<string[]>(
    graph.metrics?.length ? graph.metrics : []
  );

  // Staged single picker (resets after AddAnotherMetric)
  const [stagedIntegration, setStagedIntegration] = useState(''); // slug
  const [stagedMetric, setStagedMetric] = useState(''); // short-code

  const [periodType, setPeriodType] = useState<'daily' | 'weekly' | 'monthly'>(
    fromGranularity(graph.granularity)
  );
  const [timeRange, setTimeRange] = useState(graph.date_range);

  // ----- Design settings -----
  const [graphType, setGraphType] = useState<string>(
    graph.graph_type ?? 'auto'
  );

  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [backgroundColorType, setBackgroundColorType] = useState<
    'auto' | 'custom'
  >('auto');
  const [headingColor, setHeadingColor] = useState('#000000');
  const [headingColorType, setHeadingColorType] = useState<'auto' | 'custom'>(
    'auto'
  );
  const [fontColor, setFontColor] = useState('#000000');
  const [fontColorType, setFontColorType] = useState<'auto' | 'custom'>('auto');
  const [axisColor, setAxisColor] = useState('#cccccc');
  const [axisColorType, setAxisColorType] = useState<'auto' | 'custom'>('auto');
  const [graphWidth, setGraphWidth] = useState('');
  const [graphHeight, setGraphHeight] = useState('');

  // per-metric color config (keyed by metric short-code)
  const [metricColorTypeMap, setMetricColorTypeMap] = useState<
    Record<string, 'auto' | 'custom'>
  >({});
  const [metricColorMap, setMetricColorMap] = useState<Record<string, string>>(
    {}
  );

  // ----- Init design + per-metric configs from graph -----
  useEffect(() => {
    const gc = graph.graph_configs || {};
    const normalizeColor = (
      val?: string | 'auto'
    ): { type: 'auto' | 'custom'; value: string } =>
      !val || val === 'auto'
        ? { type: 'auto', value: '#000000' }
        : { type: 'custom', value: val };

    const bg = normalizeColor(gc.bg_color);
    setBackgroundColorType(bg.type);
    setBackgroundColor(bg.value);

    const hd = normalizeColor(gc.heading_color);
    setHeadingColorType(hd.type);
    setHeadingColor(hd.value);

    const ft = normalizeColor(gc.font_color);
    setFontColorType(ft.type);
    setFontColor(ft.value);

    const ax = normalizeColor(gc.axis_color);
    setAxisColorType(ax.type);
    setAxisColor(ax.value);

    setGraphWidth(
      gc.graph_width && gc.graph_width !== 'auto' ? String(gc.graph_width) : ''
    );
    setGraphHeight(
      gc.graph_height && gc.graph_height !== 'auto'
        ? String(gc.graph_height)
        : ''
    );

    const mc = graph.metric_configs || {};
    const initType: Record<string, 'auto' | 'custom'> = {};
    const initCol: Record<string, string> = {};
    (graph.metrics || []).forEach((m) => {
      const c = mc[m]?.color;
      if (!c || c === 'auto') {
        initType[m] = 'auto';
        initCol[m] = '#000000';
      } else {
        initType[m] = 'custom';
        initCol[m] = c;
      }
    });
    setMetricColorTypeMap(initType);
    setMetricColorMap(initCol);
  }, [graph]);

  // ----- Options -----
  const integrationOptions = useMemo(
    () =>
      (integrationsAndMetrics || []).map((itg) => ({
        text: itg.mainTitle,
        value: itg.id,
        imageIcon: itg.mainIcon,
      })),
    [integrationsAndMetrics]
  );

  const stagedMetricOptions = useMemo(() => {
    const sel = integrationsAndMetrics.find((i) => i.id === stagedIntegration);
    if (!sel) return [];
    return sel.items.map((m) => ({
      text: m.title,
      value: m.value ?? m.title,
      icon: m.icon ?? null,
    }));
  }, [stagedIntegration, integrationsAndMetrics]);

  // reset staged metric when integration changes
  useEffect(() => setStagedMetric(''), [stagedIntegration]);

  // ----- Add / remove metrics -----
  const addStagedMetric = () => {
    if (!stagedIntegration || !stagedMetric) return;
    if (metricsSelected.length >= MAX_METRICS) return;
    if (metricsSelected.includes(stagedMetric)) return;

    setMetricsSelected((prev) => [...prev, stagedMetric]);

    // set defaults for color config
    setMetricColorTypeMap((m) =>
      m[stagedMetric] ? m : { ...m, [stagedMetric]: 'auto' }
    );
    setMetricColorMap((m) =>
      m[stagedMetric] ? m : { ...m, [stagedMetric]: '#000000' }
    );

    // reset staged pickers to allow picking a NEW integration+metric
    setStagedIntegration('');
    setStagedMetric('');
  };

  const removeMetricAt = (index: number) => {
    setMetricsSelected((prev) => {
      const removed = prev[index];
      const next = prev.filter((_, i) => i !== index);
      if (removed) {
        setMetricColorTypeMap((m) => {
          const { [removed]: _a, ...rest } = m;
          return rest;
        });
        setMetricColorMap((m) => {
          const { [removed]: _b, ...rest } = m;
          return rest;
        });
      }
      return next;
    });
  };

  // ----- Labels for SelectedMetrics (derived from short-code) -----
  const getIntegrationLabelForMetric = (metricShortCode: string) => {
    const slug = slugFromShortcode(metricShortCode);
    return integrationOptions.find((o) => o.value === slug)?.text || slug || '';
  };
  const getMetricLabelForMetric = (metricShortCode: string) => {
    const slug = slugFromShortcode(metricShortCode);
    const itg = integrationsAndMetrics.find((i) => i.id === slug);
    const m = itg?.items.find((x) => (x.value ?? x.title) === metricShortCode);
    return m?.title || metricShortCode;
  };

  // ----- Validation -----
  useEffect(() => {
    const nameOk = graphName.trim().length >= 3;
    const nonEmptyMetrics = metricsSelected.filter(Boolean);
    const countOk =
      nonEmptyMetrics.length >= 1 && nonEmptyMetrics.length <= MAX_METRICS;
    const uniqueOk = new Set(nonEmptyMetrics).size === nonEmptyMetrics.length;
    const periodOk = !!periodType;
    const rangeOk = !!timeRange;

    setIsSaveDisabled(!(nameOk && countOk && uniqueOk && periodOk && rangeOk));
  }, [graphName, metricsSelected, periodType, timeRange]);

  const getTimeRangeOptions = () => {
    if (periodType === 'weekly') {
      return [
        { text: '4 Weeks', value: '4_weeks', icon: null },
        { text: '8 Weeks', value: '8_weeks', icon: null },
        { text: '12 Weeks', value: '12_weeks', icon: null },
      ];
    } else if (periodType === 'monthly') {
      return [
        { text: '3 Months', value: '3_months', icon: null },
        { text: '4 Months', value: '4_months', icon: null },
        { text: '5 Months', value: '5_months', icon: null },
        { text: 'Year to date', value: 'year_to_date', icon: null },
      ];
    }
    return [
      { text: 'Last Week', value: 'last_week', icon: null },
      { text: 'Last Month', value: 'last_month', icon: null },
      { text: '7 Days', value: '7_days', icon: null },
      { text: '14 Days', value: '14_days', icon: null },
      { text: '21 Days', value: '21_days', icon: null },
      { text: '28 Days', value: '28_days', icon: null },
      { text: '30 Days', value: '30_days', icon: null },
    ];
  };

  // ----- Save -----
  const handleSave = async () => {
    if (isSaveDisabled || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const metrics = metricsSelected.filter(Boolean);

      const metric_configs = metrics.reduce<
        Record<string, { color: string | 'auto' }>
      >((acc, m) => {
        const type = metricColorTypeMap[m] ?? 'auto';
        const color =
          type === 'auto' ? ('auto' as const) : metricColorMap[m] || '#000000';
        acc[m] = { color };
        return acc;
      }, {});

      const graph_configs = {
        bg_color:
          backgroundColorType === 'auto' ? ('auto' as const) : backgroundColor,
        heading_color:
          headingColorType === 'auto' ? ('auto' as const) : headingColor,
        font_color: fontColorType === 'auto' ? ('auto' as const) : fontColor,
        axis_color: axisColorType === 'auto' ? ('auto' as const) : axisColor,
        graph_width: graphWidth.trim()
          ? Number(graphWidth.trim())
          : ('auto' as const),
        graph_height: graphHeight.trim()
          ? Number(graphHeight.trim())
          : ('auto' as const),
      };

      // choose tara_mcp_slug from the first metric's slug
      const firstSlug = metrics.length ? slugFromShortcode(metrics[0]) : '';

      const payload: GraphUpdateRequest = {
        name: graphName.trim(),
        tara_mcp_slug: firstSlug,
        metrics,
        metric_configs,
        graph_type: graphType,
        graph_configs,
        granularity: toGranularity(periodType),
        date_range: timeRange,
        is_active: graph.is_active,
      };

      await update_graph_api_call(graph.id, payload);

      togglePopupModal(false);
      onGraphUpdated?.();
    } catch (error) {
      console.error('Error updating graph:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canAddAnotherMetric =
    metricsSelected.length < MAX_METRICS &&
    !!stagedIntegration &&
    !!stagedMetric;

  return (
    <div className='insightItemConfig reportsGraphsForm'>
      <div className='insightItemConfig__title'>
        <div className='insightItemConfig__title--main'>
          <CustomSVGs svg={editSVG} />
          <h3>{t('EditGraph', { defaultValue: 'Edit Graph' })}</h3>
        </div>
      </div>

      {/* Name */}
      <div className='insightItemConfig__inputs'>
        <AINormalInput
          changeListeners={[
            { handleInput: (e: any) => setGraphName(e.target.value) } as any,
            (e: any) => setGraphName((e as any).target.value),
          ].slice(1)} // safeguards if AINormalInput expects different signature; keep simple:
          id='graphName'
          value={graphName}
          placeholder={t('GraphName', { defaultValue: 'Graph Name' })}
          required
          type='text'
          innerPlaceholder={t('EnterGraphName', {
            defaultValue: 'Enter Graph Name',
          })}
        />
      </div>

      {/* Staged picker (add new integration+metric) */}
      {metricsSelected.length < 3 && (
        <div className='insightItemConfig__inputs'>
          <h3>{t('MetricsSelection')}</h3>

          <div className='insightItemConfig__inputs'>
            <SettingsIconSelectInput
              filterOptions={integrationOptions}
              value={stagedIntegration}
              marginTop='1.6rem'
              title={t('Integration', { defaultValue: 'Integration' })}
              onFilterChange={(val) => setStagedIntegration(val)}
              emptyOptionPlaceholder={t('SelectIntegration', {
                defaultValue: 'Select Integration',
              })}
            />

            {stagedIntegration ? (
              <SettingsIconSelectInput
                filterOptions={stagedMetricOptions}
                value={stagedMetric}
                marginTop='1.6rem'
                title={t('Metric', { defaultValue: 'Metric' })}
                onFilterChange={(v) => setStagedMetric(v)}
                emptyOptionPlaceholder={t('SelectMetric', {
                  defaultValue: 'Select Metric',
                })}
              />
            ) : null}

            <div style={{ marginTop: '1.6rem' }} className='reportsGraphs__btn'>
              <MainSquareButton
                value={
                  metricsSelected.length === 0
                    ? t('AddMetric')
                    : t('AddAnotherMetric')
                }
                type='button'
                icon={plusSignSVG}
                buttonType='white'
                disabled={isSubmitting || !canAddAnotherMetric}
                onClick={addStagedMetric}
              />
            </div>
          </div>
        </div>
      )}

      {/* Selected metrics with color controls */}
      {metricsSelected.length > 0 && (
        <div className='insightItemConfig__inputs'>
          <h3>{t('SelectedMetrics')}</h3>

          {metricsSelected.map((metricValue, idx) => (
            <div
              key={`selected-metric-${metricValue}-${idx}`}
              style={{
                marginBottom: '1rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                paddingTop: '0.5rem',
              }}
            >
              <MetricSelection
                color={metricColorMap[metricValue] || '#000000'}
                setColor={(c: string) =>
                  setMetricColorMap((m) => ({ ...m, [metricValue]: c }))
                }
                setColorType={(type: string) =>
                  setMetricColorTypeMap((m) => ({
                    ...m,
                    [metricValue]: type === 'custom' ? 'custom' : 'auto',
                  }))
                }
                colorType={metricColorTypeMap[metricValue] || 'auto'}
                integrationType={getIntegrationLabelForMetric(metricValue)}
                metricName={getMetricLabelForMetric(metricValue)}
                index={idx}
                removeMetric={() => removeMetricAt(idx)}
              />
            </div>
          ))}
        </div>
      )}

      <div className='insightItemConfig__divider' />

      {/* Time settings */}
      <div className='insightItemConfig__inputs'>
        <h3>
          {t('TimePeriodSettings', { defaultValue: 'Time Period Settings' })}
        </h3>

        <div className='reportsGraphs__inputs'>
          <div className='reportsGraphs__inputs--col1'>
            <SettingsIconSelectInput
              filterOptions={[
                {
                  text: t('PerDay', { defaultValue: 'Per Day' }),
                  value: 'daily',
                  icon: clockSVG,
                },
                // {
                //   text: t('PerWeek', { defaultValue: 'Per Week' }),
                //   value: 'weekly',
                //   icon: calendarSVG,
                // },
                {
                  text: t('PerMonth', { defaultValue: 'Per Month' }),
                  value: 'monthly',
                  icon: calendarSVG,
                },
              ]}
              marginTop='1.6rem'
              value={periodType}
              title={t('PeriodType', { defaultValue: 'Period Type' })}
              onFilterChange={(val) =>
                setPeriodType(val as 'daily' | 'weekly' | 'monthly')
              }
            />
          </div>
          <div className='reportsGraphs__inputs--col2'>
            <SettingsIconSelectInput
              filterOptions={getTimeRangeOptions()}
              marginTop='1.6rem'
              value={timeRange}
              title={t('TimeRange', { defaultValue: 'Time Range' })}
              onFilterChange={(val) => setTimeRange(val)}
              emptyOptionPlaceholder={t('SelectTimeRange', {
                defaultValue: 'Select Time Range',
              })}
            />
          </div>
        </div>
      </div>

      <div className='insightItemConfig__divider' />

      {/* Design settings */}
      <div className='insightItemConfig__inputs'>
        <h3>{t('DesignSettings')}</h3>

        <div className='reportsGraphs__inputs'>
          <div className='reportsGraphs__inputs--col1'>
            <SettingsIconSelectInput
              filterOptions={[
                { text: t('Auto'), value: 'auto', icon: null },
                { text: t('Pie'), value: 'pie', icon: null },
                { text: t('Bar'), value: 'bar', icon: null },
                { text: t('Line'), value: 'line', icon: null },
              ]}
              marginTop='1.6rem'
              value={graphType}
              title={t('GraphType')}
              onFilterChange={(val) => setGraphType(val)}
            />
          </div>
          <div className='reportsGraphs__inputs--colorCol'>
            <div className='reportsGraphs__inputs--colorCol-select'>
              <SettingsIconSelectInput
                filterOptions={[
                  { text: t('Auto'), value: 'auto', icon: null },
                  { text: t('Custom'), value: 'custom', icon: null },
                ]}
                marginTop='1.6rem'
                value={backgroundColorType}
                title={t('BackgroundColor')}
                onFilterChange={(val) =>
                  setBackgroundColorType(val as 'auto' | 'custom')
                }
              />
            </div>
            {backgroundColorType === 'custom' && (
              <div className='reportsGraphs__inputs--colorCol-color'>
                <SquareColorInput
                  id='backgroundColor'
                  setValue={setBackgroundColor}
                  value={backgroundColor}
                />
              </div>
            )}
          </div>
        </div>

        <div className='reportsGraphs__inputs'>
          <div className='reportsGraphs__inputs--colorCol'>
            <div className='reportsGraphs__inputs--colorCol-select'>
              <SettingsIconSelectInput
                filterOptions={[
                  { text: t('Auto'), value: 'auto', icon: null },
                  { text: t('Custom'), value: 'custom', icon: null },
                ]}
                marginTop='1.6rem'
                value={headingColorType}
                title={t('headingColor')}
                onFilterChange={(val) =>
                  setHeadingColorType(val as 'auto' | 'custom')
                }
              />
            </div>
            {headingColorType === 'custom' && (
              <div className='reportsGraphs__inputs--colorCol-color'>
                <SquareColorInput
                  id='headingColor'
                  setValue={setHeadingColor}
                  value={headingColor}
                />
              </div>
            )}
          </div>

          <div className='reportsGraphs__inputs--colorCol'>
            <div className='reportsGraphs__inputs--colorCol-select'>
              <SettingsIconSelectInput
                filterOptions={[
                  { text: t('Auto'), value: 'auto', icon: null },
                  { text: t('Custom'), value: 'custom', icon: null },
                ]}
                marginTop='1.6rem'
                value={fontColorType}
                title={t('FontColor')}
                onFilterChange={(val) =>
                  setFontColorType(val as 'auto' | 'custom')
                }
              />
            </div>
            {fontColorType === 'custom' && (
              <div className='reportsGraphs__inputs--colorCol-color'>
                <SquareColorInput
                  id='fontColor'
                  setValue={setFontColor}
                  value={fontColor}
                />
              </div>
            )}
          </div>
        </div>

        <div className='reportsGraphs__inputs'>
          <div className='reportsGraphs__inputs--colorCol'>
            <div className='reportsGraphs__inputs--colorCol-select'>
              <SettingsIconSelectInput
                filterOptions={[
                  { text: t('Auto'), value: 'auto', icon: null },
                  { text: t('Custom'), value: 'custom', icon: null },
                ]}
                marginTop='1.6rem'
                value={axisColorType}
                title={t('AxisColor')}
                onFilterChange={(val) =>
                  setAxisColorType(val as 'auto' | 'custom')
                }
              />
            </div>
            {axisColorType === 'custom' && (
              <div className='reportsGraphs__inputs--colorCol-color'>
                <SquareColorInput
                  id='axisColor'
                  setValue={setAxisColor}
                  value={axisColor}
                />
              </div>
            )}
          </div>
          <div className='reportsGraphs__inputs--col1'>
            <AINormalInput
              changeListeners={[(e) => setGraphWidth(e.target.value)]}
              id='graphWidth'
              value={graphWidth}
              placeholder={t('GraphWidth')}
              required={false}
              type='number'
              innerPlaceholder={t('EnterGraphWidth')}
            />
          </div>
        </div>

        <div className='reportsGraphs__inputs'>
          <div className='reportsGraphs__inputs--col1'>
            <AINormalInput
              changeListeners={[(e) => setGraphHeight(e.target.value)]}
              id='graphHeight'
              value={graphHeight}
              placeholder={t('GraphHeight')}
              required={false}
              type='number'
              innerPlaceholder={t('EnterGraphHeight')}
            />
          </div>
          <div className='reportsGraphs__inputs--col2'></div>
        </div>
      </div>

      <div className='insightItemConfig__buttons'>
        <MainSquareButton
          value={t('Cancel', { defaultValue: 'Cancel' })}
          type='button'
          buttonType='white'
          disabled={isSubmitting}
          onClick={() => togglePopupModal(false)}
        />
        <MainSquareButton
          value={
            isSubmitting
              ? t('Updating', { defaultValue: 'Updating…' })
              : t('UpdateGraph', { defaultValue: 'Update Graph' })
          }
          type='button'
          disabled={isSaveDisabled || isSubmitting}
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default EditReportsGraphs;
